import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import {
    Stethoscope, Pill, FlaskConical, ScanLine, HeartHandshake, Ambulance,
    CheckCircle2, Clock3, EyeOff, Info, Loader2, Save, Undo2,
    LayoutGrid,
    // ^ LayoutGrid does double duty: the fallback for a service id that
    // SERVICE_ICONS has no entry for (`SERVICE_ICONS[id] || LayoutGrid` — a
    // plain expression eslint's no-undef checks but the JSX checker cannot
    // see; it once shipped un-imported and threw ReferenceError), and — since
    // the tab-bar dissolution — this page's header icon, matching its sidebar
    // entry. Keep it directly after a comma: jsxcheck.py's import parser
    // cannot see a name that a comment line separates from the previous comma.
} from 'lucide-react';
import { fetchServiceVisibility, saveServiceVisibility } from '../../redux/mocSlice';
import CommonLoader from '../../components/CommonLoader';
import ErrorPage from '../../components/ErrorPage';
import useCanManage from '../../hooks/useCanManage';

// PATIENT-APP SERVICE VISIBILITY.
//
// A SEPARATE AXIS from tenants.services, and the difference is the whole point:
// this decides what a patient SEES in the Mitra One Care app, not what a hospital
// may run. Setting a service to "Coming soon" here does not stop the Super Admin
// switching it on for a tenant — getting hospitals set up before patients can see
// the service is exactly why the control exists.
//
// Ambulance comes back as "Coming soon" by default (it sits in HOLD_SERVICE_IDS
// server-side). That is a default, not a rule: it is edited like any other row,
// with no special case here.

const SERVICE_ICONS = {
    medi_consult: Stethoscope,
    pharma: Pill,
    lab: FlaskConical,
    radiology: ScanLine,
    care_taker: HeartHandshake,
    ambulance: Ambulance,
};

const STATE_OPTIONS = [
    {
        value: 'available',
        label: 'Available',
        hint: 'Patients can open and use it.',
        icon: CheckCircle2,
        active: 'bg-emerald-500 text-white border-emerald-500 shadow-sm',
    },
    {
        value: 'soon',
        label: 'Coming soon',
        hint: 'Shown, but not usable yet.',
        icon: Clock3,
        active: 'bg-[#D7A05C] text-white border-[#D7A05C] shadow-sm',
    },
    {
        value: 'hidden',
        label: 'Hidden',
        hint: 'Not shown in the app at all.',
        icon: EyeOff,
        active: 'bg-gray-500 text-white border-gray-500 shadow-sm',
    },
];

const STATE_LABEL = Object.fromEntries(STATE_OPTIONS.map((o) => [o.value, o.label]));

const StateSwitch = ({ serviceId, value, onChange, disabled }) => (
    <div className="inline-flex rounded-xl border border-gray-200 p-0.5 bg-gray-50" role="group">
        {STATE_OPTIONS.map((opt) => {
            const selected = value === opt.value;
            return (
                <button
                    key={opt.value}
                    type="button"
                    data-testid={`service-${serviceId}-${opt.value}`}
                    disabled={disabled}
                    title={opt.hint}
                    aria-pressed={selected}
                    onClick={() => onChange(opt.value)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] text-xs font-semibold border transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-60 ${selected ? opt.active : 'bg-transparent text-gray-500 border-transparent hover:text-[#2D8A8A]'
                        }`}
                >
                    <opt.icon className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">{opt.label}</span>
                </button>
            );
        })}
    </div>
);

const ServiceCard = ({ service, value, dirty, onChange, disabled }) => {
    const Icon = SERVICE_ICONS[service.id] || LayoutGrid;
    return (
        <div
            data-testid={`service-row-${service.id}`}
            className={`flex flex-col lg:flex-row lg:items-center gap-3 rounded-xl border p-4 transition-colors ${dirty ? 'border-[#4ABDBD] bg-[#4ABDBD]/5' : 'border-gray-100'
                }`}
        >
            <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="p-2.5 rounded-xl bg-gradient-to-br from-[#4ABDBD] to-[#2D8A8A] shadow-sm flex-shrink-0">
                    <Icon className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0">
                    <p className="text-sm font-bold text-gray-800">
                        {service.label}
                        {dirty && (
                            <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-[#2D8A8A] bg-[#4ABDBD]/15 px-1.5 py-0.5 rounded-md">
                                Unsaved
                            </span>
                        )}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">{service.description || '—'}</p>
                </div>
            </div>
            <StateSwitch serviceId={service.id} value={value} onChange={onChange} disabled={disabled} />
        </div>
    );
};

export default function ServiceVisibility() {
    const { t } = useTranslation('sidebar');
    const dispatch = useDispatch();
    // Without patients.manage the switches are read-only and the Save bar is
    // not drawn — the PUT would be refused anyway (hooks/useCanManage.js).
    const canManage = useCanManage('patients.manage');
    const { services, servicesLoading, servicesError, servicesSaving } = useSelector((s) => s.moc);

    // The screen edits a DRAFT and only commits on Save, so a half-made decision
    // never reaches the patient app.
    const [draft, setDraft] = useState({});

    useEffect(() => { dispatch(fetchServiceVisibility()); }, [dispatch]);

    // Re-seeded whenever the server list changes — which is only ever a load or a
    // successful save, both of which SHOULD discard the draft.
    useEffect(() => {
        setDraft(Object.fromEntries(services.map((s) => [s.id, s.state])));
    }, [services]);

    const changed = useMemo(
        () => services.filter((s) => draft[s.id] && draft[s.id] !== s.state).map((s) => ({
            id: s.id, label: s.label, from: s.state, state: draft[s.id],
        })),
        [services, draft]
    );

    const save = async () => {
        if (changed.length === 0) return;
        const { isConfirmed } = await Swal.fire({
            title: 'Apply to the patient app?',
            html: `<div style="text-align:left;font-size:13px">${changed
                .map((c) => `<div><b>${c.label}</b>: ${STATE_LABEL[c.from]} → <b>${STATE_LABEL[c.state]}</b></div>`)
                .join('')}</div><p style="font-size:12px;color:#6b7280;margin-top:10px">Hospitals are not affected — this only changes what patients see.</p>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#9DB3B1',
            confirmButtonText: 'Apply',
        });
        if (!isConfirmed) return;

        // Only what moved. The API takes a partial list and validates
        // all-or-nothing, so an untouched service is never re-written.
        const result = await dispatch(saveServiceVisibility(changed.map((c) => ({ id: c.id, state: c.state }))));
        if (saveServiceVisibility.fulfilled.match(result)) {
            toast.success('Service visibility updated');
        } else {
            toast.error(result.payload?.error || 'Failed to save service visibility');
        }
    };

    if (servicesError && services.length === 0 && !servicesLoading) {
        return <ErrorPage error={{ status: 500 }} pageName={t('sidebar.moc_services', 'Service Visibility')} />;
    }

    return (
        <div className="w-full" data-testid="moc-service-visibility">
            <div className="bg-white rounded-2xl shadow-md p-4 sm:p-6">
                {/* Page header. The MocSection tab bar used to carry this
                    screen's identity; now the page is a plain route, so it
                    names itself — icon tile + h1 + subtitle, the app's standard
                    treatment. Same icon as its sidebar entry (LayoutGrid, which
                    doubles as the unknown-service fallback above). */}
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-gradient-to-br from-[#4ABDBD] to-[#2D8A8A] rounded-xl shadow-sm flex-shrink-0">
                        <LayoutGrid className="w-5 h-5 text-white" />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-lg sm:text-xl font-bold text-gray-800 truncate">
                            {t('sidebar.moc_services', 'Services')}
                        </h1>
                        <p className="text-xs sm:text-sm text-gray-500">
                            {t('moc.services_sub', 'How the six Mitra One Care services appear inside the patient app.')}
                        </p>
                    </div>
                </div>

                {/* The distinction this screen lives or dies on. Stated on the page
                    rather than in a tooltip: "Hidden" reads like a kill switch, and
                    it is not one. */}
                <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-[#4ABDBD]/30 bg-[#4ABDBD]/5 p-3.5">
                    <Info className="w-4 h-4 text-[#2D8A8A] mt-0.5 flex-shrink-0" />
                    <div className="text-[13px] text-gray-700 leading-relaxed">
                        <p>
                            <span className="font-semibold">This controls the patient app only.</span>{' '}
                            It decides what a patient sees when they open Mitra One Care — nothing else.
                        </p>
                        <p className="mt-1.5">
                            It does <span className="font-semibold">not</span> enable or disable a service for a
                            hospital. A service set to <span className="font-semibold">Coming soon</span> or{' '}
                            <span className="font-semibold">Hidden</span> here can still be switched on for a
                            tenant under Tenants → Features — getting hospitals live before patients can see the
                            service is exactly why these are two separate switches.
                        </p>
                    </div>
                </div>

                {servicesLoading && services.length === 0 ? (
                    <div className="py-12 flex justify-center"><CommonLoader /></div>
                ) : (
                    <>
                        <div className="mt-5 space-y-3">
                            {services.map((s) => (
                                <ServiceCard
                                    key={s.id}
                                    service={s}
                                    value={draft[s.id] ?? s.state}
                                    dirty={Boolean(draft[s.id]) && draft[s.id] !== s.state}
                                    disabled={servicesSaving || !canManage}
                                    onChange={(state) => setDraft((d) => ({ ...d, [s.id]: state }))}
                                />
                            ))}
                        </div>

                        {canManage && (
                        <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
                            <span className="mr-auto text-xs text-gray-400">
                                {changed.length === 0
                                    ? 'No unsaved changes.'
                                    : `${changed.length} unsaved change${changed.length === 1 ? '' : 's'}.`}
                            </span>
                            <button
                                type="button"
                                data-testid="service-discard"
                                onClick={() => setDraft(Object.fromEntries(services.map((s) => [s.id, s.state])))}
                                disabled={changed.length === 0 || servicesSaving}
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-gray-300 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <Undo2 className="w-4 h-4" /> Discard
                            </button>
                            <button
                                type="button"
                                data-testid="service-save"
                                onClick={save}
                                disabled={changed.length === 0 || servicesSaving}
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#4ABDBD] text-white text-sm font-semibold shadow hover:bg-[#3A9B9B] transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {servicesSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                Save changes
                            </button>
                        </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
